<template>
  <div
    id="primary-navigation"
    class="sidebar"
    :class="{ collapsed: effectiveCollapsed, 'mobile-open': mobileOpen }"
  >
    <div class="logo">
      <div class="logo-icon" @click="effectiveCollapsed && toggleCollapse()">Y</div>
      <span v-if="!effectiveCollapsed" class="logo-title">阅泽AI 图片站</span>
      <img
        v-if="!effectiveCollapsed"
        class="desktop-collapse-button"
        src="@/assets/image/close.png"
        alt="收起"
        @click="toggleCollapse"
      />
      <button
        type="button"
        class="mobile-close-button"
        aria-label="关闭主导航"
        @click="$emit('close-mobile')"
      >
        <i class="el-icon-close" aria-hidden="true"></i>
      </button>
    </div>

    <div class="menu-list">
      <div v-for="item in menuList" :key="item.path || item.name">
        <!-- 一级菜单 -->
        <div
          :class="$route.path === item.path ? 'active' : 'menu-item'"
          v-if="!item.name"
          @click="handleMenuClick(item)"
          @mouseenter="onMouseEnter(item.path)"
          @mouseleave="onMouseLeave"
        >
          <!-- 这里换成 svg-icon -->
          <img v-if="isImageURL(getIcon(item))" :src="getIcon(item)" class="menu-image-icon" alt="" />
          <svg-icon v-else :name="getIcon(item)" style="width: 15px" class="icon" />
          <span v-if="!effectiveCollapsed" class="title">{{ item.title }}</span>
        </div>

        <!-- 分组菜单 -->
        <div class="menu-group" style="border-bottom: solid 1px #eaeff3" v-else>
          <div v-if="!effectiveCollapsed" class="menu-title">{{ item.name }}</div>
          <div style="padding: 10px 0">
            <div
              :style="effectiveCollapsed ? {} : { padding: '0 0 0 42px' }"
              v-for="items in item.list"
              :key="items.path"
              @click.stop="handleMenuClick(items)"
              @mouseenter="onMouseEnter(items.path)"
              @mouseleave="onMouseLeave"
              :class="$route.path === items.path ? 'active' : 'menu-item'"
            >
              <img v-if="isImageURL(getIcon(items))" :src="getIcon(items)" class="menu-image-icon" alt="" />
              <svg-icon
                v-else
                :name="getIcon(items)"
                style="width: 15px"
                class="icon"
              />
              <span v-if="!effectiveCollapsed" class="title">{{ items.title }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer">
      <div>
        <el-popover
          popper-class="customer-popover"
          placement="right"
          width="300px"
          trigger="manual"
          v-model="customerServiceVisible"
        >
          <div
            ref="customerServiceTrigger"
            slot="reference"
            style="margin-bottom: 4px"
            @click="customerServiceVisible = !customerServiceVisible"
            class="footer-item"
          >
            <svg-icon
              :name="getCustomerServiceIcon(footerList[0])"
              style="width: 15px"
              class="icon"
            />
            <span v-if="!effectiveCollapsed" class="title">{{ footerList[0].title }}</span>
            <span v-if="feedbackUnreadCount > 0" class="unread-badge">{{ feedbackUnreadCount > 99 ? '99+' : feedbackUnreadCount }}</span>
          </div>
          <div class="customer-title">联系客服</div>
          <div class="qrcode-box">
            <div class="customer-contact-row">
              <div class="qrcode">
                <img src="@/assets/image/customer-service-qrcode.jpg" alt="微信扫码" class="qrcode" />
                <div>扫一扫</div>
              </div>
              <div class="time">
                <div>官方客服</div>
                <div>工作时间:</div>
                <div>工作日9:00~18:00</div>
              </div>
            </div>
            <div class="customer-reward-tip">添加客服，领取2000积分奖励</div>
          </div>
          <div
            v-if="feedbackUnreadCount > 0"
            class="feedback-notify-banner"
            @click="goToFeedbackPage"
          >
            <i class="el-icon-chat-dot-round" style="margin-right: 6px"></i>
            您有 {{ feedbackUnreadCount }} 条新的反馈回复，点击查看
          </div>
          <div class="customer-title" style="margin: 18px 0 15px">意见反馈</div>
          <el-input
            type="textarea"
            v-model="feedbackText"
            :rows="6"
            placeholder="请描述您遇到的问题或建议..."
            class="customer-input"
          />
          <el-input v-model="feedbackPhone" placeholder="联系方式（可选）" />
          <el-button type="primary" class="submit-btn" :loading="feedbackSubmitting" @click="handleSubmitFeedback">提交</el-button>
        </el-popover>
        <div
          style="margin-bottom: 4px"
          :class="isPersonalCenterActive ? 'footer-item' : 'footer-user'"
          @click="handleFooterClick(footerList[1])"
          @mouseenter="onMouseEnter(footerList[1].path)"
          @mouseleave="onMouseLeave"
        >
          <svg-icon
            :name="getFooterIcon(footerList[1])"
            style="width: 15px"
            class="icon"
          />
          <span v-if="!effectiveCollapsed" class="title">{{ footerList[1].title }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { submitFeedback, fetchMyFeedbackUnreadCount, fetchAdminFeedbackUnreadCount } from "@/services/feedback";
import { authState } from "@/services/auth";
import { listToolApps } from "@/services/toolApps";
import { buildSidebarToolList, isSidebarImageURL } from "@/utils/sidebarTools";

const ALL_APPS_MENU_ITEM = {
  title: "全部应用",
  path: "/all-apps",
  icon: "all-apps",
  iconActive: "all-apps-active",
  iconHover: "all-apps-hover",
};

export default {
  name: "sidebar-view",
  props: {
    mobileOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      collapsed: false,
      hoverPath: "",
      customerServiceVisible: false,
      feedbackText: "",
      feedbackPhone: "",
      feedbackSubmitting: false,
      feedbackUnreadCount: 0,
      homeToolList: [],
      homeToolConfigLoaded: false,
      baseMenuList: [
        {
          title: "首页",
          path: "/home",
          icon: "home",
          iconActive: "home-active",
          iconHover: "home-hover",
        },
        {
          title: "图像 AI 生成",
          path: "/ai-image",
          icon: "ai-image",
          iconActive: "ai-image-active",
          iconHover: "ai-image-hover",
        },
        {
          title: "图像 AI 编辑",
          path: "/ai-edit",
          icon: "ai-edit",
          iconActive: "ai-edit-active",
          iconHover: "ai-edit-hover",
        },
        {
          title: "商品详情图",
          path: "/goods-detail",
          icon: "scene",
          iconActive: "scene-active",
          iconHover: "scene-hover",
        },
        {
          name: "工具箱",
          list: [
            {
              title: "产品精修",
              path: "/product-retouch",
              icon: "product-retouch",
              iconActive: "product-retouch-active",
              iconHover: "product-retouch-hover",
            },
            {
              title: "图片去水印",
              path: "/watermark-remove",
              icon: "watermark-remove",
              iconActive: "watermark-remove-active",
              iconHover: "watermark-remove-hover",
            },
            {
              title: "智能抠图",
              path: "/cutout",
              icon: "cutout",
              iconActive: "cutout-active",
              iconHover: "cutout-hover",
            },
            {
              title: "模特生成",
              path: "/model-generate",
              icon: "model-generate",
              iconActive: "model-generate-active",
              iconHover: "model-generate-hover",
            },
            {
              title: "商品替换",
              path: "/product-replace",
              icon: "product-replace",
              iconActive: "product-replace-active",
              iconHover: "product-replace-hover",
            },
            {
              title: "服装替换",
              path: "/clothes-replace",
              icon: "clothes-replace",
              iconActive: "clothes-replace-active",
              iconHover: "clothes-replace-hover",
            },
            ALL_APPS_MENU_ITEM,
          ],
        },
        {
          title: "会员",
          path: "/vip",
          icon: "vip",
          iconActive: "vip-active",
          iconHover: "vip-hover",
        },
        {
          title: "创作中心",
          path: "/creation-center",
          icon: "creation-center",
          iconActive: "creation-center-active",
          iconHover: "creation-center-hover",
        },
        {
          title: "发布作品",
          path: "/community/publish",
          icon: "home",
          iconActive: "home-active",
          iconHover: "home-hover",
        },
        {
          title: "团队协作",
          path: "/team-project",
          icon: "team-project",
          iconActive: "team-project-active",
          iconHover: "team-project-hover",
        },
        {
          name: "创作工具",
          list: [
            {
              title: "AI 写作生成",
              path: "https://writer.yuezecloud.com",
              external: true,
              icon: "inspire-assistant",
              iconActive: "inspire-assistant-active",
              iconHover: "inspire-assistant-hover",
            },
            {
              title: "AI 视频生成",
              path: "https://video.yuezecloud.com",
              external: true,
              icon: "video-generate",
              iconActive: "video-generate-active",
              iconHover: "video-generate-hover",
            },
          ],
        },
        {
          name: "AI实验室",
          list: [
            {
              title: "灵感助手",
              path: "/inspire-assistant",
              icon: "inspire-assistant",
              iconActive: "inspire-assistant-active",
              iconHover: "inspire-assistant-hover",
            },
            {
              title: "视频生成",
              path: "/video-generate",
              icon: "video-generate",
              iconActive: "video-generate-active",
              iconHover: "video-generate-hover",
            },
            {
              title: "提示词大全",
              path: "/prompt-library",
              icon: "inspire-assistant",
              iconActive: "inspire-assistant-active",
              iconHover: "inspire-assistant-hover",
            },
          ],
        },
      ],
      footerList: [
        {
          title: "客服",
          path: "/customer-service",
          icon: "customer-service-active",
          iconHover: "customer-service-hover",
          iconActive: "customer-service-active",
        },
        {
          title: "个人中心",
          path: "/personal-center",
          icon: "personal-center",
          iconHover: "personal-center-hover",
          iconActive: "personal-center-active",
        },
      ],
    };
  },
  computed: {
    effectiveCollapsed() {
      return this.collapsed && !this.mobileOpen;
    },
    menuList() {
      return this.baseMenuList
        .filter((item) => !item.hidden)
        .map((item) => {
          if (!Array.isArray(item.list)) {
            return item;
          }
          if (item.name === "工具箱" && this.homeToolConfigLoaded) {
            return {
              ...item,
              list: [...this.homeToolList, ALL_APPS_MENU_ITEM].filter((child) => !child.hidden),
            };
          }
          return {
            ...item,
            list: item.list.filter((child) => !child.hidden),
          };
        })
        .filter((item) => !Array.isArray(item.list) || item.list.length > 0);
    },
    currentSession() {
      return authState.session;
    },
    isPersonalCenterActive() {
      return this.$route.path === "/personal-center";
    },
  },
  methods: {
    toggleCollapse() {
      this.collapsed = !this.collapsed;
      this.$emit("collapse", this.collapsed);
    },
    isImageURL(value) {
      return isSidebarImageURL(value);
    },
    handleMenuClick(item) {
      if (!item || !item.path) {
        return;
      }
      this.$emit("navigate");
      if (item.path === "/all-apps") {
        this.$bus && this.$bus.$emit("openAllDrawer", true);
        return;
      }
      if (item.path === "/publish") {
        this.$bus && this.$bus.$emit("openPublishDialog", true);
        return;
      }
      if (item.path === "/vip") {
        this.$emit("request-member");
        return;
      }
      if (item.external) {
        window.open(item.path, "_blank", "noopener,noreferrer");
        return;
      }
      this.$router.push(item.path);
    },
    async loadHomeToolConfig() {
      try {
        const appPayload = await listToolApps({ enabled: true, show_in_home: true });
        const apps = Array.isArray(appPayload && appPayload.items) ? appPayload.items : [];
        this.homeToolList = buildSidebarToolList(apps);
        this.homeToolConfigLoaded = true;
      } catch (_) {
        this.homeToolConfigLoaded = false;
      }
    },
    getIcon(item) {
      if (this.$route.path === item.path) return item.iconActive;
      if (this.hoverPath === item.path) return item.iconHover;
      return item.icon;
    },
    getFooterIcon(item) {
      if (this.$route.path === item.path && item.iconActive) {
        return item.iconActive;
      }
      if (this.hoverPath === item.path && item.iconHover) {
        return item.iconHover;
      }
      return item.icon;
    },
    getCustomerServiceIcon(item) {
      if (this.customerServiceVisible) return item.iconActive;
      if (this.hoverPath === item.path) return item.iconHover;
      return item.icon;
    },
    handleFooterClick(item) {
      if (!item || !item.path) {
        return;
      }
      if (item.path === "/personal-center") {
        this.$emit("navigate");
        this.$router.push(item.path);
        return;
      }
    },
    onMouseEnter(path) {
      this.hoverPath = path;
    },
    onMouseLeave() {
      this.hoverPath = "";
    },
    async handleSubmitFeedback() {
      const content = (this.feedbackText || "").trim();
      if (!content) {
        this.$message.warning("请输入反馈内容");
        return;
      }
      this.feedbackSubmitting = true;
      try {
        await submitFeedback(content, this.feedbackPhone);
        this.$message.success("反馈已提交，感谢您的意见");
        this.feedbackText = "";
        this.feedbackPhone = "";
        this.customerServiceVisible = false;
      } catch (e) {
        this.$message.error((e && e.message) || "提交失败");
      } finally {
        this.feedbackSubmitting = false;
      }
    },
    async loadFeedbackUnreadCount() {
      try {
        const session = authState && authState.session;
        if (!session || !session.id) return;
        if (session.user && session.user.is_platform_admin) {
          this.feedbackUnreadCount = await fetchAdminFeedbackUnreadCount();
        } else {
          this.feedbackUnreadCount = await fetchMyFeedbackUnreadCount();
        }
      } catch (_) { /* ignore */ }
    },
    handleClickOutside(event) {
      const popoverEl = document.querySelector('.customer-popover');
      const triggerEl = this.$refs.customerServiceTrigger;
      if (
        (popoverEl && popoverEl.contains(event.target)) ||
        (triggerEl && triggerEl.contains(event.target))
      ) {
        return;
      }
      this.customerServiceVisible = false;
    },
    goToFeedbackPage() {
      this.customerServiceVisible = false;
      this.$router.push({ path: '/user-detail', query: { tab: 'feedback' } });
    },
  },
  mounted() {
    this.loadFeedbackUnreadCount();
    this.loadHomeToolConfig();
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  },
  watch: {
    currentSession(val) {
      if (val && val.id) this.loadFeedbackUnreadCount();
      else this.feedbackUnreadCount = 0;
    },
    '$route.path': function () {
      this.loadFeedbackUnreadCount();
    },
    customerServiceVisible(val) {
      if (val) {
        document.addEventListener('mousedown', this.handleClickOutside);
      } else {
        document.removeEventListener('mousedown', this.handleClickOutside);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:global(.customer-popover) {
  margin-left: 30px !important;
  padding: 28px 22px !important;
  width: 300px !important;
  box-shadow: 0px 0px 30px 0px rgba(91, 118, 148, 0.3) !important;
  border-radius: 12px !important;
}
.sidebar {
  height: 100vh;
  background: #fff;
  border-right: 1px solid #eee;
  position: absolute;
  left: 0;
  top: 0;
  width: 210px;
  transition: width 0.25s ease;
  overflow-y: auto;
}

.sidebar.collapsed {
  width: 60px;
}

.sidebar.collapsed .menu-item,
.sidebar.collapsed .active {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.sidebar.collapsed .menu-group > div {
  padding-left: 0 !important;
}

.sidebar.collapsed .menu-group .menu-item,
.sidebar.collapsed .menu-group .active {
  padding-left: 0 !important;
  justify-content: center;
}

.sidebar.collapsed .logo {
  justify-content: center;
  cursor: pointer;
}

.menu-list {
  height: calc(100vh - 165px);
  overflow: hidden;
  overflow-y: auto;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 15px;
  margin-bottom: 8px;
  color: #fff;
  font-weight: bold;
  background-image: linear-gradient(111deg, #fe752b 0%, #ff9805 100%);
  font-size: 16px;

  .logo-icon {
    width: 32px;
    height: 32px;
    background: #ffae22;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo-title {
    min-width: 0;
    flex: 1 1 auto;
    font-size: 16px;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.desktop-collapse-button {
  width: 16px;
  margin-top: 4px;
  cursor: pointer;
}

.mobile-close-button {
  display: none;
}

.menu-title {
  height: 50px;
  background-color: #f2f5f8;
  display: flex;
  align-items: center;
  color: #9ea3ad;
  font-size: 14px;
  padding-left: 14px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  font-size: 14px;
  height: 36px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: #ffefd2;
    margin: 0 12px;
    color: #f78001;
    border-radius: 8px;
  }
}

.active {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  font-size: 14px;
  height: 36px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #313845;
  color: #fff;
  font-weight: 500;
  border-radius: 8px;
}

.menu-image-icon {
  width: 15px;
  height: 15px;
  border-radius: 4px;
  object-fit: cover;
  flex: 0 0 15px;
}

.footer {
  border-top: solid 1px #eaeff3;
  padding: 7px 12px;
  font-size: 14px;

  .footer-user {
    border-radius: 8px;
    padding: 9px 11px;
    background-color: rgba(211, 219, 234, 0.27);
    cursor: pointer;
    gap: 4px;
    display: flex;
    align-items: center;

    &:hover {
      color: #f78001;
    }
  }

  .footer-item {
    background-color: #ff8432;
    border-radius: 8px;
    padding: 9px 11px;
    color: #ffffff;
    gap: 6px;
    margin-bottom: 5px;
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  .footer-icon {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }
}
.customer-title {
  font-size: 16px;
  color: #333333;
  border-left: 5px solid #fe8431;
  padding-left: 6px;
}
.feedback-notify-banner {
  margin: 14px 0 0;
  padding: 10px 14px;
  background: #fff7f0;
  border: 1px solid #ffe0c2;
  border-left: 3px solid #f78001;
  border-radius: 8px;
  color: #f78001;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #fff0e0;
  }
}
.qrcode-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 12px;
  border-bottom: solid 1px #dddddd;
  .customer-contact-row {
    display: flex;
    gap: 16px;
  }
  .time {
    div {
      font-size: 12px;
      line-height: 20px;
      color: #666666;
    }
    div:nth-child(1) {
      font-size: 14px;
      color: #333333;
      line-height: 30px;
    }
  }
  .customer-reward-tip {
    padding: 6px 10px;
    border-radius: 6px;
    background: #fff3e8;
    color: #ff6a00;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-align: center;
  }
  .qrcode {
    width: 76px;
    div {
      font-size: 12px;
      line-height: 20px;
      color: #aaaaaa;
      text-align: center;
    }
    img {
      width: 76px;
      height: 76px;
    }
  }
}
.customer-input {
  width: 100%;
  background: #f1f5f8 !important;
  border-radius: 8px;
  margin-bottom: 13px;
}
.customer-input ::v-deep .el-textarea__inner {
  background-color: #f1f5f8;
  border: none;
}
.submit-btn {
  width: 100%;
  margin-top: 22px;
  height: 42px;
  background-color: #ff8432;
  box-shadow: 0px 10px 13px 0px rgba(255, 114, 0, 0.3);
  border-radius: 21px;
}
.unread-badge {
  margin-left: auto;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  font-size: 11px;
  color: #fff;
  background: #f56c6c;
  border-radius: 9px;
  padding: 0 5px;
}

@media (max-width: 767px) {
  .sidebar {
    position: fixed;
    top: 0;
    z-index: 3100;
    width: min(280px, calc(100vw - 40px));
    max-width: 280px;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    box-shadow: 8px 0 24px rgba(0, 0, 0, 0.16);
  }

  .sidebar.mobile-open {
    transform: translateX(0);
  }

  .sidebar.collapsed {
    width: min(280px, calc(100vw - 40px));
  }

  .desktop-collapse-button {
    display: none;
  }

  .mobile-close-button {
    display: inline-flex;
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #ffffff;
    cursor: pointer;
    font-size: 18px;
  }
}
</style>
